import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

import { type TurnstileWidgetHandle } from "@/components/TurnstileWidget";
import { ContributorList } from "@/components/results/ContributorList";
import { RepoList } from "@/components/results/RepoList";
import { ResultsSummary } from "@/components/results/ResultsSummary";
import { RecentSearchList } from "@/components/search/RecentSearchList";
import { SearchError } from "@/components/search/SearchError";
import { SearchForm } from "@/components/search/SearchForm";
import { SearchRejected } from "@/components/search/SearchRejected";
import { SearchStatus } from "@/components/search/SearchStatus";
import { SearchSessionProvider, useSearchSession } from "@/context/search-session";
import { ApiError, createSearch, getSearch, toUiError } from "@/lib/api";
import { loadRecentSearches, removeRecentSearch, saveRecentSearches, upsertPendingRecentSearch, upsertRecentSearchResponse, type RecentSearchRecord } from "@/lib/recent-searches";
import { dedupeSearchedRepos } from "@/lib/repos";
import { getSearchHomePath, getSearchPath, parseAppRoute } from "@/lib/routes";
import { trackPageview, trackRecentSearchOpen, trackSampleQueryClick, trackSearchCompleted, trackSearchFailed, trackSearchRejected, trackSearchSubmitted } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import "./index.css";
import type { SearchResponse } from "@/lib/types";

function trackSearchResult(searchId: string, response: SearchResponse) {
  if (response.status === "completed") {
    trackSearchCompleted(searchId, response.results?.length ?? 0);
  } else if (response.status === "rejected") {
    trackSearchRejected(searchId);
  } else if (response.status === "failed") {
    trackSearchFailed(searchId, response.error_code ?? undefined);
  }
}

function normalizeMaxRepos(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { error: "Choose a repository count between 1 and 20." };
  }

  if (!/^\d+$/.test(trimmed)) {
    return { error: "Max repositories must be a whole number." };
  }

  const parsed = Number.parseInt(trimmed, 10);
  return { value: Math.max(1, Math.min(parsed, 20)) };
}
const sampleQueries = [
  "Rust async runtime contributors",
  "React design system engineers",
  "Go distributed systems contributors",
  "Python ML infrastructure contributors",
  "Postgres performance engineers",
];

const loadForceGraph = () => import("@/components/results/ForceGraph").then(module => ({ default: module.ForceGraph }));
const LazyForceGraph = lazy(loadForceGraph);

function Experience() {
  const { state, dispatch } = useSearchSession();
  const [route, setRoute] = useState(() => parseAppRoute(window.location.pathname));
  const [recentSearches, setRecentSearches] = useState<RecentSearchRecord[]>([]);
  const [isAnnouncementDismissed, setIsAnnouncementDismissed] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);
  const pendingTurnstileSubmitRef = useRef(false);
  const repos = useMemo(() => dedupeSearchedRepos(state.response?.repos_searched ?? []), [state.response?.repos_searched]);

  const updateRecentSearches = useCallback((updater: (current: RecentSearchRecord[]) => RecentSearchRecord[]) => {
    setRecentSearches(current => {
      const next = updater(current);
      saveRecentSearches(next);
      return next;
    });
  }, []);

  const navigate = useCallback((path: string, replace = false) => {
    if (replace) {
      window.history.replaceState({}, "", path);
    } else {
      window.history.pushState({}, "", path);
    }

    setRoute(parseAppRoute(path));
    trackPageview(path);
  }, []);

  useEffect(() => {
    setRecentSearches(loadRecentSearches());
    trackPageview();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setRoute(parseAppRoute(window.location.pathname));
      trackPageview();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/") {
      navigate(getSearchHomePath(), true);
    }
  }, [navigate]);

  useEffect(() => {
    if (route.kind === "search") {
      void loadForceGraph();
    }
  }, [route.kind]);

  const recoverSearch = useCallback(
    async (searchId: string) => {
      dispatch({ type: "START_RECOVERY", searchId });
      try {
        const response = await getSearch(searchId);
        dispatch({ type: "HYDRATE_RESPONSE", response });
        updateRecentSearches(current => upsertRecentSearchResponse(current, response));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          updateRecentSearches(current => removeRecentSearch(current, searchId));
          dispatch({ type: "CLEAR_SEARCH" });
          dispatch({ type: "SET_INFO", message: "Search not found." });
          navigate(getSearchHomePath(), true);
          return;
        }

        dispatch({ type: "SET_FAILURE", error: toUiError(error), searchId });
      }
    },
    [dispatch, navigate, updateRecentSearches],
  );

  useEffect(() => {
    if (route.kind === "search") {
      const alreadyShowing = state.searchId === route.searchId && (state.status === "submitting" || state.response?.search_id === route.searchId);
      if (!alreadyShowing) {
        void recoverSearch(route.searchId);
      }
      return;
    }

    if (route.kind === "home" && state.searchId && state.status !== "idle") {
      dispatch({ type: "CLEAR_SEARCH" });
    }
  }, [dispatch, recoverSearch, route, state.response, state.searchId, state.status]);

  useEffect(() => {
    if (route.kind !== "search") {
      return;
    }

    if (state.searchId !== route.searchId) {
      return;
    }

    if (state.status !== "pending" && state.status !== "running") {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void recoverSearch(route.searchId);
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [recoverSearch, route, state.searchId, state.status]);

  const submitSearch = useCallback(
    async ({ query, maxRepos, token }: { query: string; maxRepos: number; token: string }) => {
      pendingTurnstileSubmitRef.current = false;

      const searchId = crypto.randomUUID();
      updateRecentSearches(current => upsertPendingRecentSearch(current, searchId, query));
      dispatch({ type: "START_SUBMIT", query, searchId });
      trackSearchSubmitted(query, maxRepos);
      navigate(getSearchPath(searchId));

      try {
        const response = await createSearch({
          search_id: searchId,
          query,
          turnstile_token: token,
          max_repos: maxRepos,
        });
        dispatch({ type: "HYDRATE_RESPONSE", response });
        updateRecentSearches(current => upsertRecentSearchResponse(current, response));
        trackSearchResult(searchId, response);
      } catch (error) {
        try {
          const recovered = await getSearch(searchId);
          dispatch({ type: "HYDRATE_RESPONSE", response: recovered });
          updateRecentSearches(current => upsertRecentSearchResponse(current, recovered));
          trackSearchResult(searchId, recovered);
        } catch {
          dispatch({ type: "SET_FAILURE", error: toUiError(error), searchId });
          trackSearchFailed(searchId, toUiError(error)?.errorCode);
        }
      } finally {
        pendingTurnstileSubmitRef.current = false;
        turnstileRef.current?.reset();
        dispatch({ type: "SET_TURNSTILE_TOKEN", token: null });
        dispatch({ type: "SET_TURNSTILE_VISIBLE", visible: false });
      }
    },
    [dispatch, navigate, updateRecentSearches],
  );

  const handleSubmit = useCallback(async () => {
    if (state.configError) {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_FORM_ERROR", message: state.configError });
      return;
    }

    const query = state.query.trim();
    if (!query) {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_FORM_ERROR", message: "Enter a search query." });
      return;
    }

    if (query.length > 200) {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_FORM_ERROR", message: "Queries must be 200 characters or fewer." });
      return;
    }

    const maxRepos = normalizeMaxRepos(state.maxReposInput);
    if (maxRepos.error || typeof maxRepos.value !== "number") {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_FORM_ERROR", message: maxRepos.error ?? "Invalid repository count." });
      return;
    }

    if (!state.turnstileToken) {
      pendingTurnstileSubmitRef.current = true;
      dispatch({ type: "SET_TURNSTILE_VISIBLE", visible: true });
      dispatch({ type: "SET_FORM_ERROR", message: "Complete verification to continue." });
      return;
    }

    await submitSearch({ query, maxRepos: maxRepos.value, token: state.turnstileToken });
  }, [dispatch, state.configError, state.maxReposInput, state.query, state.turnstileToken, submitSearch]);

  const handleQueryChange = useCallback(
    (query: string) => {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_QUERY", query });
    },
    [dispatch],
  );

  const handleMaxReposChange = useCallback(
    (value: string) => {
      pendingTurnstileSubmitRef.current = false;
      dispatch({ type: "SET_MAX_REPOS", value });
    },
    [dispatch],
  );

  const handleTurnstileTokenChange = useCallback(
    (token: string | null) => {
      dispatch({ type: "SET_TURNSTILE_TOKEN", token });
      if (!token || !pendingTurnstileSubmitRef.current) {
        return;
      }

      const query = state.query.trim();
      const maxRepos = normalizeMaxRepos(state.maxReposInput);
      if (!query || maxRepos.error || typeof maxRepos.value !== "number") {
        pendingTurnstileSubmitRef.current = false;
        return;
      }

      void submitSearch({ query, maxRepos: maxRepos.value, token });
    },
    [dispatch, state.maxReposInput, state.query, submitSearch],
  );

  const openSearch = useCallback(
    (searchId: string) => {
      dispatch({ type: "SET_INFO", message: null });
      trackRecentSearchOpen(searchId);
      navigate(getSearchPath(searchId));
    },
    [dispatch, navigate],
  );

  const resetToSearch = useCallback(() => {
    pendingTurnstileSubmitRef.current = false;
    dispatch({ type: "CLEAR_SEARCH" });
    dispatch({ type: "SET_INFO", message: null });
    navigate(getSearchHomePath());
  }, [dispatch, navigate]);

  const isBusy = state.status === "submitting" || state.status === "pending" || state.status === "running";
  const response = state.response;
  const handleDismissAnnouncement = useCallback(() => setIsAnnouncementDismissed(true), []);

  return (
    <div className="paper-stage">
      {!isAnnouncementDismissed ? (
        <div className="announcement-ribbon">
          <div className="announcement-ribbon-inner">
            <p className="announcement-copy">
              <span>Hiring engineers?</span>
              <a href="https://forms.withsurface.com/s/cmm8kbpgr00lrjs04vb6imqgs" className="surface-form-button">
                Get started with SkillSync.
              </a>
            </p>
            <button
              type="button"
              className="announcement-close"
              aria-label="Dismiss announcement"
              onClick={handleDismissAnnouncement}
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="px-3 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4">
        <div className="paper-page mx-auto flex min-h-[calc(100vh-1.5rem)] max-w-7xl flex-col px-8 py-10 sm:px-12 sm:py-12 lg:px-16">
          {route.kind === "not-found" ? (
            <main className="mx-auto flex min-h-[60vh] w-full max-w-2xl items-center">
              <Card className="w-full bg-background/40">
                <CardHeader>
                  <CardTitle>Page not found</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => navigate(getSearchHomePath(), true)}>Back to search</Button>
                </CardContent>
              </Card>
            </main>
          ) : null}

          {route.kind === "home" ? (
            <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col justify-center gap-12 py-8">
              <section className="space-y-[3.25rem] sm:space-y-[3.75rem] text-center">
                <div className="space-y-3">
                  <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">Find elite but overlooked engineers</h1>
                </div>

                <SearchForm
                  query={state.query}
                  maxReposInput={state.maxReposInput}
                  isBusy={isBusy}
                  formError={state.formError}
                  configError={state.configError}
                  turnstileSiteKey={state.appConfig?.turnstileSiteKey ?? null}
                  turnstileRef={turnstileRef}
                  showTurnstile={state.showTurnstile}
                  onQueryChange={handleQueryChange}
                  onMaxReposChange={handleMaxReposChange}
                  onSubmit={handleSubmit}
                  onTurnstileTokenChange={handleTurnstileTokenChange}
                  onTurnstileError={message => dispatch({ type: "SET_FORM_ERROR", message })}
                />

                <div className="mx-auto flex w-full max-w-5xl flex-wrap justify-center gap-3">
                  {sampleQueries.map(sampleQuery => (
                    <button
                      key={sampleQuery}
                      type="button"
                      className="rounded-full border border-slate-300/80 bg-white/70 px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-400 hover:bg-white"
                      onClick={() => {
                        trackSampleQueryClick(sampleQuery);
                        dispatch({ type: "SET_QUERY", query: sampleQuery });
                        window.requestAnimationFrame(() => {
                          const input = document.getElementById("query");
                          if (input instanceof HTMLInputElement) {
                            input.focus();
                            input.setSelectionRange(input.value.length, input.value.length);
                          }
                        });
                      }}
                    >
                      {sampleQuery}
                    </button>
                  ))}
                </div>

                {state.infoMessage ? <p className="text-sm text-muted-foreground">{state.infoMessage}</p> : null}
              </section>

              <RecentSearchList searches={recentSearches} onOpen={openSearch} />
            </main>
          ) : null}

          {route.kind === "search" ? (
            <main className="space-y-8 py-2">
              <ResultsSummary
                title={response?.query_title || response?.query || state.query || "Search"}
                query={response?.query || state.query}
                onBack={resetToSearch}
              />

              {(state.status === "submitting" || state.status === "pending" || state.status === "running") ? (
                <SearchStatus />
              ) : null}

              {state.status === "rejected" && response ? <SearchRejected reason={response.reason ?? "Search unavailable."} onReset={resetToSearch} /> : null}

              {state.status === "failed" ? (
                <SearchError
                  message={response?.error_message ?? state.error?.message ?? "The search could not be completed."}
                  errorCode={response?.error_code ?? state.error?.errorCode}
                  onReset={resetToSearch}
                />
              ) : null}

              {state.status === "completed" && response ? (
                <div className="grid gap-8 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
                  <ContributorList
                    contributors={response.results}
                    highlightedContributor={state.highlightedContributor}
                    onHoverContributor={login => dispatch({ type: "SET_HIGHLIGHT", contributor: login })}
                  />
                  <div className="space-y-8">
                    <Suspense
                      fallback={
                        <Card>
                          <CardHeader>
                            <CardTitle>Contributor Graph</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="graph-surface flex items-center justify-center text-sm text-muted-foreground">Loading graph…</div>
                          </CardContent>
                        </Card>
                      }
                    >
                      <LazyForceGraph
                        repos={repos}
                        contributors={response.results}
                        highlightedRepo={state.highlightedRepo}
                        highlightedContributor={state.highlightedContributor}
                        onHoverRepo={repo => dispatch({ type: "SET_HIGHLIGHT", repo })}
                        onHoverContributor={contributor => dispatch({ type: "SET_HIGHLIGHT", contributor })}
                      />
                    </Suspense>
                    <RepoList
                      repos={repos}
                      highlightedRepo={state.highlightedRepo}
                      onHoverRepo={repo => dispatch({ type: "SET_HIGHLIGHT", repo })}
                    />
                  </div>
                </div>
              ) : null}
            </main>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <SearchSessionProvider>
      <Experience />
    </SearchSessionProvider>
  );
}

export default App;
